import Card from 'v2/components/Card';
import CardActionLinkButton from 'v2/components/CardActionLinkButton';
import CardHeader from 'v2/components/CardHeader';
import ConfirmModal from 'v2/components/ConfirmModal';
import DataTable from 'v2/components/DataTable';
import EmptyState from 'v2/components/EmptyState';
import ExpiredPill from 'v2/components/ExpiredPill';
import LoadingBar from 'v2/components/LoadingBar';
import OutlinedButton from 'v2/components/OutlinedButton';
import RowActionsMenu from 'v2/components/RowActionsMenu';
import usePesticideLicensesTable from 'v2/layouts/OrganizationWorkspaceLayout/components/OrganizationDetailsSection/components/PesticideLicensesCard/hooks/usePesticideLicensesTable';

import { getIsExpired } from '@@utils/dates';
import { getLocalDateString } from '@@utils/getLocalDateString';

import HorizontalContainer from 'components/containers/HorizontalContainer';

import useTranslation from './hooks/useTranslation';

import {
    ACTIONS_COLUMN_KEY,
    ACTIONS_COLUMN_LABEL,
    ACTIONS_COLUMN_WIDTH,
    DATE_LOCALE,
    EMPTY_CELL_TEXT,
    EXPIRATION_DATE_COLUMN_KEY,
    ISSUED_DATE_COLUMN_KEY,
    LICENSE_DATE_FORMAT_OPTIONS,
    LICENSE_NUMBER_COLUMN_KEY,
    STATE_COLUMN_KEY,
    TEST_ID
} from './constants';
import type { DataTableColumn, DataTableRow, FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const PesticideLicensesCard: FunctionComponent<Props> = ({ className = '', testId = TEST_ID }) => {
    const {
        canManage,
        handleAdd,
        handleDeleteCancel,
        handleDeleteConfirm,
        handleDeleteRequest,
        handleEdit,
        handleLoadMore,
        hasNextPage,
        isDeleteConfirmOpen,
        isFetchingMore,
        isLoading,
        licenses
    } = usePesticideLicensesTable();

    const {
        ACTIONS_LABEL,
        ADD_LICENSE,
        DELETE,
        DELETE_CONFIRM_CANCEL,
        DELETE_CONFIRM_LABEL,
        DELETE_CONFIRM_MESSAGE,
        DELETE_CONFIRM_TITLE,
        EDIT,
        EMPTY_DESCRIPTION,
        EMPTY_HEADING,
        EXPIRATION_DATE,
        EXPIRED,
        ISSUED_DATE,
        LICENSE_NUMBER,
        LOAD_MORE,
        STATE,
        TABLE_LABEL,
        TITLE
    } = useTranslation();

    const columns: DataTableColumn[] = [
        { key: STATE_COLUMN_KEY, label: STATE },
        { key: LICENSE_NUMBER_COLUMN_KEY, label: LICENSE_NUMBER },
        { key: ISSUED_DATE_COLUMN_KEY, label: ISSUED_DATE },
        { key: EXPIRATION_DATE_COLUMN_KEY, label: EXPIRATION_DATE },
        ...(canManage
            ? [
                  {
                      key: ACTIONS_COLUMN_KEY,
                      label: ACTIONS_COLUMN_LABEL,
                      width: ACTIONS_COLUMN_WIDTH
                  }
              ]
            : [])
    ];

    const rows: DataTableRow[] = licenses.map(license => ({
        ...(canManage
            ? {
                  [ACTIONS_COLUMN_KEY]: (
                      <RowActionsMenu
                          actions={[
                              { label: EDIT, onClick: () => handleEdit(license.id) },
                              {
                                  isDestructive: true,
                                  label: DELETE,
                                  onClick: () => handleDeleteRequest(license.id)
                              }
                          ]}
                          ariaLabel={ACTIONS_LABEL}
                      />
                  )
              }
            : {}),
        [EXPIRATION_DATE_COLUMN_KEY]: (
            <HorizontalContainer className={styles.expirationCell}>
                {license.expirationDate
                    ? getLocalDateString(
                          license.expirationDate,
                          DATE_LOCALE,
                          LICENSE_DATE_FORMAT_OPTIONS
                      )
                    : EMPTY_CELL_TEXT}

                {getIsExpired(license.expirationDate) ? <ExpiredPill text={EXPIRED} /> : null}
            </HorizontalContainer>
        ),
        [ISSUED_DATE_COLUMN_KEY]: license.issuedDate
            ? getLocalDateString(license.issuedDate, DATE_LOCALE, LICENSE_DATE_FORMAT_OPTIONS)
            : EMPTY_CELL_TEXT,
        [LICENSE_NUMBER_COLUMN_KEY]: license.licenseNumber || EMPTY_CELL_TEXT,
        [STATE_COLUMN_KEY]: license.state || EMPTY_CELL_TEXT
    }));

    return (
        <Card className={className} testId={testId}>
            <CardHeader
                action={
                    canManage ? (
                        <CardActionLinkButton onClick={handleAdd} text={ADD_LICENSE} />
                    ) : undefined
                }
                title={TITLE}
            />

            {isLoading ? <LoadingBar /> : null}

            {!isLoading && licenses.length === 0 ? (
                <EmptyState compact description={EMPTY_DESCRIPTION} heading={EMPTY_HEADING} />
            ) : null}

            {!isLoading && licenses.length > 0 ? (
                <div className={styles.tableScroll}>
                    <DataTable ariaLabel={TABLE_LABEL} columns={columns} rows={rows} />
                </div>
            ) : null}

            {hasNextPage ? (
                <OutlinedButton
                    className={styles.loadMore}
                    disabled={isFetchingMore}
                    onClick={handleLoadMore}
                >
                    {LOAD_MORE}
                </OutlinedButton>
            ) : null}

            <ConfirmModal
                cancelLabel={DELETE_CONFIRM_CANCEL}
                confirmLabel={DELETE_CONFIRM_LABEL}
                isDestructive
                isOpen={isDeleteConfirmOpen}
                message={DELETE_CONFIRM_MESSAGE}
                onCancel={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title={DELETE_CONFIRM_TITLE}
            />
        </Card>
    );
};

export default PesticideLicensesCard;
